import { As, Box, Flex, Spacer, useBreakpointValue, useColorModeValue } from '@chakra-ui/react';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/20/solid';
import { ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@main/complykit/components/button';
import { Divider } from '@main/complykit/components/divider';
import { Tooltip } from '@main/complykit/components/tooltip';
import { useRouter } from '@tanstack/react-router';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { IconAction, IconActionComponentProps } from '../actions';
import { MenuActions } from '../actions/menu-actions';
import { Action, MenuActionItem } from '../actions/types';
import { useDrawer } from './use-drawer';

/** Press-invoked onClick: no event, and presses do not bubble (see actions/types.ts). */
export type DrawerAction = Action & { onClick?: () => void | Promise<unknown> };

export type DrawerActionsProps = {
  primaryAction?: DrawerAction | null;
  secondaryActions?: DrawerAction[] | ReactNode;
  iconActions?: IconActionComponentProps[];
  menuActions?: MenuActionItem[];
};

export const DrawerActions = (props: DrawerActionsProps & { info?: ReactNode }) => {
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: 'md' });
  const bgColor = useColorModeValue('white', 'gray.700');

  return (
    <Box
      role="toolbar"
      aria-label="Drawer actions"
      position="sticky"
      top={0}
      zIndex={1}
      bg={bgColor}
    >
      {isMobile ? <DrawerActionsMobile {...props} /> : <DrawerActionsDesktop {...props} />}
    </Box>
  );
};

const DrawerActionsMobile = (props: DrawerActionsProps) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Flex
      gap={3}
      py={3}
      pl={4}
      pr={5}
      borderBottom="1px"
      borderColor={borderColor}
      alignItems="center"
    >
      <CloseDrawerButton icon={ChevronLeftIcon} />
      <Spacer />
      <ToolbarActions {...props} />
    </Flex>
  );
};

const DrawerActionsDesktop = ({ info, ...actions }: DrawerActionsProps & { info?: ReactNode }) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Flex
      height={12}
      justifyContent="space-between"
      borderBottom="1px"
      borderColor={borderColor}
      py={2}
      pl={4}
      pr={5}
    >
      <NavigationButtons />
      <Flex gap={3} alignItems="center">
        {info}
        <ToolbarActions {...actions} />
        <CloseDrawerButton icon={XMarkIcon} />
      </Flex>
    </Flex>
  );
};

const CloseDrawerButton = ({ icon }: { icon: As }) => {
  const { t } = useTranslation('ui');
  const drawer = useDrawer();

  return (
    <IconAction tooltip={t('drawer.actions.close')} icon={icon} onClick={() => drawer.close()} />
  );
};

const NavigationButtons = () => {
  const { t } = useTranslation('ui');
  const router = useRouter();
  const canGoBack = window.navigation?.canGoBack ?? true;
  const canGoForward = window.navigation?.canGoForward ?? true;

  return (
    // gap 2: the sm squares carry their own breathing room, 12px read airy.
    <Flex gap={2}>
      <IconAction
        tooltip={t('drawer.actions.back')}
        isDisabled={!canGoBack}
        icon={ArrowLeftIcon}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        onClick={() => router.history.back()}
      />
      <IconAction
        tooltip={t('drawer.actions.forward')}
        isDisabled={!canGoForward}
        icon={ArrowRightIcon}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        onClick={() => router.history.forward()}
      />
    </Flex>
  );
};

const ToolbarActions = ({
  primaryAction,
  secondaryActions,
  iconActions,
  menuActions,
}: DrawerActionsProps) => {
  const hasSecondaryActions = Array.isArray(secondaryActions)
    ? secondaryActions.length > 0
    : Boolean(secondaryActions);

  return (
    <>
      <Flex gap={2} alignItems="center">
        {Array.isArray(secondaryActions)
          ? secondaryActions.map((props) => (
              <ButtonAction key={props.label} {...props} variant="outline" />
            ))
          : secondaryActions}
        {primaryAction && <ButtonAction {...primaryAction} variant="solid" />}
      </Flex>
      {(hasSecondaryActions || primaryAction) && <Divider orientation="vertical" />}
      {iconActions?.map((props) => <IconAction key={props.tooltip} {...props} />)}
      <MenuActions actions={menuActions} />
    </>
  );
};

type ActionButtonProps = DrawerAction & {
  variant: 'solid' | 'outline';
};

const ButtonAction = ({
  label,
  tooltip,
  isDisabled,
  disabledTooltip,
  isLoading,
  variant,
  onClick,
}: ActionButtonProps) => {
  /* The tooltip shows only while disabled, exactly as before: an enabled
   * toolbar button wears its name, so echoing it would only add noise. The
   * seat context flips the disabled ComplyKit Button to its aria-disabled
   * form, which is what keeps the reason keyboard-reachable. */
  const content = isDisabled ? (disabledTooltip ?? tooltip) : undefined;

  return (
    <Tooltip content={content ?? ''} isDisabled={!content}>
      <Button
        variant={variant}
        isDisabled={isDisabled}
        isPending={isLoading}
        onPress={() => onClick?.()}
      >
        {label}
      </Button>
    </Tooltip>
  );
};
